import React, { useState, useEffect, useCallback, useRef } from 'react';
import InactivityWarningModal from '../components/common/InactivityWarningModal';
import { useSystemConfigStore } from '../stores/useSystemConfigStore';
import { useAuth } from './useAuth';
import { useInjectedService } from './useInjectedService';

/**
 * useInactivityTimeout Hook
 * 
 * Manages user inactivity session timeout in a platform-agnostic way.
 * Consumes the injected IEventService to monitor user activity across platforms.
 * 100% Type-safe and adheres to project mandates.
 */
export const useInactivityTimeout = () => {
  const { user, logout } = useAuth();
  const { security } = useSystemConfigStore();
  const eventService = useInjectedService('events');
  const sessionTimeoutMinutes = security?.sessionTimeoutMinutes || 30; // Default to 30 mins

  const [isWarningVisible, setIsWarningVisible] = useState(false);
  const [countdown, setCountdown] = useState(60); // 60-second warning

  const timeoutId = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningTimeoutId = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownIntervalId = useRef<ReturnType<typeof setInterval> | null>(null);

  const handleLogout = useCallback(() => {
    logout();
  }, [logout]);

  const startWarningCountdown = useCallback(() => {
    setIsWarningVisible(true);
    setCountdown(60);

    if (countdownIntervalId.current) clearInterval(countdownIntervalId.current);

    countdownIntervalId.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          if (countdownIntervalId.current) clearInterval(countdownIntervalId.current);
          handleLogout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [handleLogout]);

  const resetTimeouts = useCallback(() => {
    setIsWarningVisible(false);
    if (timeoutId.current) clearTimeout(timeoutId.current);
    if (warningTimeoutId.current) clearTimeout(warningTimeoutId.current);
    if (countdownIntervalId.current) clearInterval(countdownIntervalId.current);

    if (user && sessionTimeoutMinutes > 0) {
      const warningTime = (sessionTimeoutMinutes * 60 - 60) * 1000; // 1 minute before timeout
      const logoutTime = sessionTimeoutMinutes * 60 * 1000;

      if (warningTime > 0) {
        warningTimeoutId.current = setTimeout(startWarningCountdown, warningTime);
      }
      timeoutId.current = setTimeout(handleLogout, logoutTime);
    }
  }, [user, sessionTimeoutMinutes, handleLogout, startWarningCountdown]);

  const handleStayLoggedIn = () => {
    resetTimeouts();
  };

  useEffect(() => {
    // 100% dynamic: subscribe to user activity via injected service
    const unsubscribe = eventService.subscribeToUserActivity(() => {
      resetTimeouts();
    });

    // Initial setup
    resetTimeouts();

    return () => {
      unsubscribe();
      if (timeoutId.current) clearTimeout(timeoutId.current);
      if (warningTimeoutId.current) clearTimeout(warningTimeoutId.current);
      if (countdownIntervalId.current) clearInterval(countdownIntervalId.current);
    };
  }, [eventService, resetTimeouts]);

  if (!isWarningVisible || !user) {
    return null;
  }

  return React.createElement(InactivityWarningModal, {
    countdown: countdown,
    onStayLoggedIn: handleStayLoggedIn,
    onLogout: handleLogout,
  });
};
